/**
 * Per-charger firmware status timeline (Phase H).
 *
 * Backend: GET /firmware/:chargerId/timeline (default limit 200).
 * One row per FirmwareStatusNotification reported by the charger.
 */

import React, { useCallback, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import { fetchFirmwareTimeline } from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import { firmwareStatusColor, formatOcppTimestamp } from '../utils/statusFormat';
import type { FirmwareEvent, GetFirmwareTimelineResponse } from '../models';
import type { OcppTabProps } from './types';

const FirmwareTab: React.FC<OcppTabProps> = ({ chargerId }) => {
  const [events, setEvents] = useState<FirmwareEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchFirmwareTimeline(chargerId);
    if (isOcppSuccess(res.status)) {
      const payload = res.payload as GetFirmwareTimelineResponse | null;
      setEvents(payload?.events ?? []);
      setError(null);
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setLoading(false);
  }, [chargerId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <OcppLoadingState label="Loading firmware history…" />;
  if (error) return <OcppErrorBanner error={error} onRetry={load} />;

  if (events.length === 0) {
    return (
      <OcppEmptyState
        icon="memory"
        title="No firmware events"
        description="The charger has not reported a firmware status notification yet. Trigger one from the Remote Control tab if needed."
      />
    );
  }

  const latest = events[0];

  return (
    <View>
      <OcppSectionCard
        title="Latest"
        subtitle={`Recorded ${formatOcppTimestamp(latest.occurred_at)}`}
        trailing={
          <OcppStatusPill label={latest.status} color={firmwareStatusColor(latest.status)} />
        }
      >
        {latest.progress_percent !== null ? (
          <View style={styles.progressRow}>
            <View style={styles.progressTrack}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${Math.max(0, Math.min(100, latest.progress_percent ?? 0))}%`,
                    backgroundColor: firmwareStatusColor(latest.status),
                  },
                ]}
              />
            </View>
            <Text style={styles.progressLabel}>{latest.progress_percent}%</Text>
          </View>
        ) : null}
        {latest.message ? <Text style={styles.message}>{latest.message}</Text> : null}
        {latest.group_id !== null ? (
          <Text style={styles.subline}>Rollout group #{latest.group_id}</Text>
        ) : null}
      </OcppSectionCard>

      <OcppSectionCard title="Timeline" subtitle="Most recent first.">
        <Pressable
          onPress={load}
          accessibilityRole="button"
          accessibilityLabel="Refresh firmware timeline"
          style={({ pressed }) => [styles.refresh, pressed && { opacity: 0.7 }]}
        >
          <Text style={styles.refreshText}>↻ Refresh</Text>
        </Pressable>
        {events.map((evt) => (
          <View key={evt.id} style={styles.eventRow}>
            <View style={[styles.dot, { backgroundColor: firmwareStatusColor(evt.status) }]} />
            <View style={{ flex: 1 }}>
              <View style={styles.eventHeader}>
                <Text style={styles.eventTitle}>{evt.status}</Text>
                <Text style={styles.eventTime}>{formatOcppTimestamp(evt.occurred_at)}</Text>
              </View>
              {evt.message ? <Text style={styles.eventMsg}>{evt.message}</Text> : null}
              {evt.progress_percent !== null ? (
                <Text style={styles.eventProgress}>Progress {evt.progress_percent}%</Text>
              ) : null}
            </View>
          </View>
        ))}
      </OcppSectionCard>
    </View>
  );
};

export default FirmwareTab;

const styles = StyleSheet.create({
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginVertical: 8,
  },
  progressTrack: {
    flex: 1,
    height: 6,
    backgroundColor: OcppTheme.surfaceAlt,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  progressLabel: {
    color: OcppTheme.textPrimary,
    fontSize: 12,
    fontWeight: '700',
    minWidth: 38,
    textAlign: 'right',
  },
  message: {
    color: OcppTheme.textSecondary,
    fontSize: 13,
    marginTop: 4,
  },
  subline: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginTop: 4,
  },
  refresh: {
    alignSelf: 'flex-end',
    marginBottom: 8,
  },
  refreshText: {
    color: OcppTheme.accent,
    fontSize: 12,
    fontWeight: '700',
  },
  eventRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    gap: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.divider,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginTop: 6,
  },
  eventHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  eventTitle: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '700',
  },
  eventTime: {
    color: OcppTheme.textDim,
    fontSize: 11,
  },
  eventMsg: {
    color: OcppTheme.textSecondary,
    fontSize: 12,
    marginTop: 2,
  },
  eventProgress: {
    color: OcppTheme.textMuted,
    fontSize: 11,
    marginTop: 2,
  },
});
